import React, { useMemo, useCallback, useState } from 'react';
import { 
  useGetConditionResourcesLookupsQuery,
  useConditionResourceFilters,
  useConditionResourceActions,
  ConditionResourceDetailsModal,
  createPrescriptionsColumns,
  createPrescriptionsActions,
  useResourceBulkChangeStatusMutation,
  ConditionResourceLookup,
  CONDITION_RESOURCES_FEATURES,
} from '@entities/condition-resources';
import { ConditionResourceCreateModal, ConditionResourceEditModal } from '@entities/condition-resources/components/ConditionResourceCreateEditModal';
import { Box, VStack } from '@chakra-ui/react';
import { Pill, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import SearchAndFiltersSection, { Filter } from '@/components/section/SearchAndFiltersSection';
import EntityTableSection from '@/components/section/EntityTableSection';
import type { BulkAction } from '@/components/section/EntityTableSection';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ErrorAlert from '@/components/ErrorAlert';
import Pagination from '@/components/ui/Pagination';
import { LOOKUP_STATUS_FILTER_OPTIONS, LOOKUP_STATUS_OPTIONS, getStatusBadgeProps } from '@/utils/lookupStatus';
import type { LookupStatus } from '@/utils/lookupStatus';
import { useMultiSelect } from '@/hooks/useMultiSelect';
import { useModal } from '@/hooks/useModal';

const SeedPrescriptionsPage: React.FC = () => {
  // Performance optimized with memoized hooks and callbacks
  const { filters, queryParams, handleFilterChange } = useConditionResourceFilters();
  const actions = useConditionResourceActions();
  
  const {
    data: paginatedResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetConditionResourcesLookupsQuery({
    ...queryParams,
    type: 'prescription',
  }, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const resourcesArray = useMemo(() => paginatedResponse?.data || [], [paginatedResponse?.data]);
  const filteredResources = resourcesArray; // API handles filtering, so we use the data directly

  // Multi-select
  const multiSelect = useMultiSelect(resourcesArray);
  const [bulkChangeStatus, { isLoading: isBulkUpdating }] = useResourceBulkChangeStatusMutation();
  const bulkConfirmModal = useModal();
  const [pendingBulkStatus, setPendingBulkStatus] = useState<LookupStatus | null>(null);

  const requestBulkStatusChange = useCallback((status: LookupStatus) => {
    setPendingBulkStatus(status);
    bulkConfirmModal.open();
  }, [bulkConfirmModal]);

  const handleBulkStatusConfirm = useCallback(async () => {
    if (!pendingBulkStatus) return;
    const ids = Array.from(multiSelect.selectedIds) as number[];
    try {
      await bulkChangeStatus({ ids, status: pendingBulkStatus }).unwrap();
      multiSelect.clearSelection();
      bulkConfirmModal.close();
      setPendingBulkStatus(null);
      refetch();
    } catch (err) {
      console.error('Bulk status update failed:', err);
    }
  }, [pendingBulkStatus, multiSelect.selectedIds, bulkChangeStatus, multiSelect.clearSelection, bulkConfirmModal, refetch]);

  const handleBulkConfirmClose = useCallback(() => {
    bulkConfirmModal.close();
    setPendingBulkStatus(null);
  }, [bulkConfirmModal]);

  const bulkActions: BulkAction[] = useMemo(() => {
    if (multiSelect.selectedCount === 0) return [];
    return [
      {
        key: 'changeStatus',
        label: 'Change Status',
        icon: RefreshCw,
        items: LOOKUP_STATUS_OPTIONS.map((opt) => ({
          key: opt.value,
          label: opt.label,
          onClick: () => requestBulkStatusChange(opt.value as LookupStatus),
        })),
      },
    ];
  }, [multiSelect.selectedCount, requestBulkStatusChange]);

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => 
    createPrescriptionsColumns(), []
  );

  // Memoize table actions
  const tableActions = useMemo(() => 
    createPrescriptionsActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler to prevent inline function re-renders
  const handleSearchChange = useCallback(
    (value: string) => {
      handleFilterChange("search", value);
    },
    [handleFilterChange]
  );

  // Memoize filter change handlers to prevent recreating on every render
  const handleStatusFilterChange = useCallback(
    (value: string) => handleFilterChange("status", value),
    [handleFilterChange]
  );
  
  const handleValidationStatusFilterChange = useCallback(
    (value: string) => handleFilterChange("validationStatus", value),
    [handleFilterChange]
  );

  // Memoize validation status options to prevent recreating array on every render
  const validationStatusOptions = useMemo(
    () => [
      { value: "all", label: "All Validation" },
      { value: "valid", label: "Valid" },
      { value: "invalid", label: "Invalid" },
      { value: "unknown", label: "Unknown" },
    ],
    []
  );

  // Memoize search filters configuration
  const searchFilters: Filter[] = useMemo(
    () => [
      ...(CONDITION_RESOURCES_FEATURES.SHOW_STATUS_FILTER ? [{
        key: "status" as const,
        label: "All Status",
        value: filters.status,
        options: LOOKUP_STATUS_FILTER_OPTIONS,
        onChange: handleStatusFilterChange,
      }] : []),
      {
        key: "validationStatus",
        label: "All Validation",
        value: filters.validationStatus,
        options: validationStatusOptions,
        onChange: handleValidationStatusFilterChange,
      },
    ],
    [filters.status, filters.validationStatus, handleStatusFilterChange, handleValidationStatusFilterChange, validationStatusOptions]
  );

  // Memoize modal success handler to refresh data after create/update
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Memoize page change handler
  const handlePageChange = useCallback((page: number) => {
    handleFilterChange('page', page);
  }, [handleFilterChange]);

  // Memoize page size change handler
  const handlePageSizeChange = useCallback((pageSize: number) => {
    handleFilterChange('limit', pageSize);
  }, [handleFilterChange]);

  if (isLoading && !resourcesArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load prescriptions"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto">
      <VStack gap={6} align="stretch">
        <PageHeader
          title="Seed Prescriptions"
          subtitle={`${filteredResources.length} prescriptions (${paginatedResponse?.total || 0} total)`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Add Prescription"
          bulkActions={CONDITION_RESOURCES_FEATURES.SHOW_BULK_SELECT ? bulkActions : undefined}
          selectedCount={multiSelect.selectedCount}
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search prescriptions by title or description..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection<ConditionResourceLookup>
          data={filteredResources}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: Pill,
            title: "No prescription content found",
            description: "Create your first prescription to get started",
          }}
          multiSelect={CONDITION_RESOURCES_FEATURES.SHOW_BULK_SELECT ? {
            selectedIds: multiSelect.selectedIds,
            onToggle: multiSelect.toggle,
            onToggleAll: multiSelect.toggleAll,
            isAllSelected: multiSelect.isAllSelected,
            getItemId: (item) => item.id,
          } : undefined}
        />

        {/* Pagination */}
        {paginatedResponse && (
          <Pagination
            paginationInfo={{
              current_page: paginatedResponse.current_page,
              last_page: paginatedResponse.last_page,
              per_page: paginatedResponse.per_page,
              total: paginatedResponse.total,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            isLoading={isFetching}
          />
        )}

        {/* Hash-managed Modals - Each modal self-determines if it should render */}
        <ConditionResourceDetailsModal />

        <ConditionResourceCreateModal
          resourceType="prescription"
          onSuccess={handleModalSuccess}
        />

        <ConditionResourceEditModal
          resourceType="prescription"
          onSuccess={handleModalSuccess}
        />

        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {bulkConfirmModal.isOpen && pendingBulkStatus && (
          <ConfirmationModal
            isOpen={bulkConfirmModal.isOpen}
            onClose={handleBulkConfirmClose}
            onConfirm={handleBulkStatusConfirm}
            title="Confirm Bulk Status Change"
            message={`Are you sure you want to change ${multiSelect.selectedCount} prescription${multiSelect.selectedCount > 1 ? 's' : ''} to "${getStatusBadgeProps(pendingBulkStatus).label}"?`}
            confirmText="Change Status"
            isLoading={isBulkUpdating}
            type="warning"
          />
        )}
      </VStack>
    </Box>
  );
};

export default SeedPrescriptionsPage;
